import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {fetchRegionByIdThunk, fetchEstates, fetchSubRegions, clearRegionError, clearRegionData} from 'shared/stores/store';
import NewestEstates from "./Estates/EstateGrid"
import { useTranslation } from 'react-i18next';
import RegionGrid from "./Widgets/RegionGrid";

export default function RegionDetailPage() {
    const { t, i18n } = useTranslation('common');
    const { id } = useParams();
    const dispatch = useDispatch();
    const region = useSelector((state) => state.regions.current);
    const subRegions = useSelector((state) => state.regions.subRegions);
    const allEstates = useSelector((state) => state.estates.list);
    const loading = useSelector((state) => state.regions.loading);
    const error = useSelector((state) => state.regions.error);
    const currentLocale = i18n.language;

    useEffect(() => {
        // Clear any existing errors when component mounts
        dispatch(clearRegionError());
        
        const fetchData = async () => {
            try {
                await dispatch(fetchRegionByIdThunk(id)).unwrap();
                dispatch(fetchEstates());
                dispatch(fetchSubRegions(id));
            } catch (err) {
                // Errors will be handled by the Redux store
                console.error("Error fetching region data:", err);
            }
        };
        
        fetchData();
        
        // Clean up when component unmounts
        return () => {
            dispatch(clearRegionError());
        };
    }, [dispatch, id]);

    // Filter estates for this region
    const regionEstates = allEstates.filter(estate => estate.region_id === parseInt(id));

    // Function to get localized content
    const getLocalizedContent = (fieldName) => {
        // If the region has _locales and a translation exists for current locale
        if (region?._locales && region._locales[currentLocale] && region._locales[currentLocale][fieldName]) {
            return region._locales[currentLocale][fieldName];
        }
        // Fallback to default content
        return region?.[fieldName] || '';
    };

    // Get localized description
    const getLocalizedDescription = () => {
        return getLocalizedContent('description');
    };

    // Get localized annual events
    const getLocalizedAnnualEvents = () => {
        return getLocalizedContent('annual_events');
    };

    // Get localized notable landmarks
    const getLocalizedNotableLandmarks = () => {
        return getLocalizedContent('notable_landmarks');
    };

    // Handle loading state
    if (loading && !region) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center py-10">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-sky-600 mb-4"></div>
                    <p className="text-gray-600">{t('common.loading')}</p>
                </div>
            </div>
        );
    }

    // Handle error state - only show error if it's not a 422 error
    // 422 errors are usually validation errors which are not relevant for this page
    if (error && !error.includes('422') && !region) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center py-10 max-w-lg mx-auto">
                    <div className="bg-red-50 p-4 rounded-lg">
                        <h3 className="text-lg font-medium text-red-800 mb-2">{t('common.error')}</h3>
                        <p className="text-sm text-red-700">{t('region.errorLoading')}</p>
                        <button 
                            onClick={() => {
                                dispatch(clearRegionError());
                                window.location.reload();
                            }} 
                            className="mt-4 px-4 py-2 bg-red-100 text-red-800 rounded hover:bg-red-200 transition-colors"
                        >
                            {t('common.tryAgain')}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // If no region data is available, show a not found message
    if (!region && !loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center py-10">
                    <p className="text-xl text-gray-600">{t('region.notFound')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white">
            {/* Region Header */}
            <div className="relative h-[calc(100vh-4rem)]">
                {/* Background Image */}
                <div
                    className="absolute inset-0 overflow-hidden"
                    style={{
                        backgroundImage: `url(${region.image1_original || ''})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center'
                    }}
                >
                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent"/>
                </div>

                {/* Content Container - Positioned absolutely at bottom */}
                <div className="absolute bottom-0 left-0 right-0 pb-16">
                    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4">{region.name}</h1>
                        <p className="text-lg text-white max-w-3xl">{getLocalizedDescription()}</p>
                    </div>
                </div>
            </div>

            <div className="mx-auto max-w-2xl px-4 py-24 sm:px-6 sm:py-12 lg:max-w-7xl lg:px-8">
                <div>
                    <div className="mx-auto max-w-2xl lg:max-w-none mt-24">
                        <div className="text-center">
                            <h2 className="text-balance text-xl font-semibold tracking-tight text-gray-900 sm:text-4xl">
                                {t('region.about.title', { region: region.name })}
                            </h2>
                            <p className="mt-4 text-lg leading-8 text-gray-600">
                                {t('region.about.subtitle', { region: region.name })}
                            </p>
                        </div>
                        <dl className="mt-16 grid grid-cols-1 gap-0.5 overflow-hidden rounded-2xl text-center sm:grid-cols-2 lg:grid-cols-4">
                            <div className="flex flex-col bg-gray-400/5 p-8">
                                <dt className="text-sm font-semibold leading-6 text-gray-600">
                                    {t('region.statistics.area')}
                                </dt>
                                <dd className="order-first text-3xl font-semibold tracking-tight text-gray-900">
                                    {region.area_km2} km²
                                </dd>
                            </div>
                            <div className="flex flex-col bg-gray-400/5 p-8">
                                <dt className="text-sm font-semibold leading-6 text-gray-600">
                                    {t('region.statistics.population')}
                                </dt>
                                <dd className="order-first text-3xl font-semibold tracking-tight text-gray-900">
                                    {region.population?.toLocaleString()}
                                </dd>
                            </div>
                            <div className="flex flex-col bg-gray-400/5 p-8">
                                <dt className="text-sm font-semibold leading-6 text-gray-600">
                                    {t('region.statistics.avgTemp')}
                                </dt>
                                <dd className="order-first text-3xl font-semibold tracking-tight text-gray-900">
                                    {region.avg_temperature} °C
                                </dd>
                            </div>
                            <div className="flex flex-col bg-gray-400/5 p-8">
                                <dt className="text-sm font-semibold leading-6 text-gray-600">
                                    {t('region.statistics.currency')}
                                </dt>
                                <dd className="order-first text-3xl font-semibold tracking-tight text-gray-900">
                                    {t('region.statistics.currencyValue')}
                                </dd>
                            </div>
                        </dl>
                    </div>

                    {/* Features sections */}
                    <dl className="mt-16 grid grid-cols-1 gap-x-6 gap-y-10 sm:grid-cols-2 sm:gap-y-16 lg:grid-cols-3 lg:gap-x-8">
                        <div className="border-gray-200">
                            <dt className="font-medium text-gray-900">{t('region.features.landmarks')}</dt>
                            <dd className="mt-2 text-sm text-gray-500">{getLocalizedNotableLandmarks()}</dd>
                        </div>

                        <div className="border-gray-200">
                            <dt className="font-medium text-gray-900">{t('region.features.events')}</dt>
                            <dd className="mt-2 text-sm text-gray-500">{getLocalizedAnnualEvents()}</dd>
                        </div>

                        <div className="border-gray-200">
                            <dt className="font-medium text-gray-900">{t('region.features.climate.title')}</dt>
                            <dd className="mt-2 text-sm text-gray-500">
                                {t('region.features.climate.description', {
                                    region: region.name,
                                    avgTemp: region.avg_temperature
                                })}
                            </dd>
                        </div>

                        <div className="border-t border-gray-200 pt-4">
                            <dt className="font-medium text-gray-900">{t('region.features.transport')}</dt>
                            <dd className="mt-2 text-sm text-gray-500">
                                {t('region.features.airport')}: {region.nearest_airport}
                            </dd>
                        </div>

                        <div className="border-t border-gray-200 pt-4">
                            <dt className="font-medium text-gray-900">{t('region.features.amenities.title')}</dt>
                            <dd className="mt-2 text-sm text-gray-500">
                                {t('region.features.amenities.restaurants')}: {region.restaurants_count}
                                <br/>{t('region.features.amenities.shoppingCenters')}: {region.shopping_centers_count}
                                <br/>{t('region.features.amenities.parks')}: {region.parks_count}
                                <br/>{t('region.features.amenities.beaches')}: {region.beaches_count}
                            </dd>
                        </div>

                        <div className="border-t border-gray-200 pt-4">
                            <dt className="font-medium text-gray-900">{t('region.features.quality.title')}</dt>
                            <dd className="mt-2 text-sm text-gray-500">
                                {t('region.features.quality.safety')}: {region.safety_index}/100
                                <br/>{t('region.features.quality.healthcare')}: {region.healthcare_quality}
                                <br/>{t('region.features.quality.education')}: {region.education_quality}
                                <br/>{t('region.features.quality.transport')}: {region.public_transport_rating}
                            </dd>
                        </div>
                    </dl>
                </div>
            </div>

            {/* Estates Grid */}
            <div className="bg-gray-50">
                <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center mb-8">
                        <h2 className="text-2xl font-bold">
                            {t('region.estates.title', { region: region.name })}
                        </h2>
                    </div>
                    <NewestEstates regionId={region.id} />
                </div>
            </div>

            {/* SubRegions section */}
            {subRegions?.length > 0 && (
                <div className="bg-white py-24">
                    <RegionGrid
                        regions={subRegions}
                        title={t('region.subRegions.title', { region: region.name })}
                        description={t('region.subRegions.description', { region: region.name })}
                    />
                </div>
            )}
        </div>
    );
}